import React, { useEffect, useRef, useState } from 'react';
import { useInView, useMotionValue, useSpring } from 'motion/react';
import { Box } from '@mui/material';
import type { BoxProps } from '@mui/material';
import { useMotionSafe } from './useMotionSafe';
import { IN_VIEW_MARGIN } from './variants';
import { APP_LOCALE } from '../utils/locale';

interface CounterProps extends Omit<BoxProps, 'children'> {
  /** Final value to count up to. */
  value: number;
  /** Rendered before/after the number, e.g. "+", "%", "k". */
  prefix?: string;
  suffix?: string;
  /** Decimal places to show. */
  decimals?: number;
}

/**
 * Counts up to `value` when scrolled into view — used for marketing stat
 * bands. Renders the final number immediately under reduced motion.
 */
export const Counter: React.FC<CounterProps> = ({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  ...boxProps
}) => {
  const { enabled } = useMotionSafe();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: IN_VIEW_MARGIN });

  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { stiffness: 60, damping: 20 });
  const [display, setDisplay] = useState(enabled ? 0 : value);

  useEffect(() => {
    if (enabled && inView) {
      motionValue.set(value);
    }
  }, [enabled, inView, motionValue, value]);

  useEffect(() => {
    if (!enabled) {
      setDisplay(value);
      return;
    }
    return spring.on('change', (latest) => setDisplay(latest));
  }, [enabled, spring, value]);

  return (
    <Box component="span" ref={ref} {...boxProps}>
      {prefix}
      {display.toLocaleString(APP_LOCALE, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </Box>
  );
};

export default Counter;
